import React from "react";
import { URLS } from "Utils/apiURL";
import { ENRICH_ACCOUNT_FIELDS } from "Pages/Dashboard/data-toolkit/constants";
import StatusPill from "Pages/Dashboard/data-toolkit/StatusPill";
import ToolRunner from "Pages/Dashboard/data-toolkit/ToolRunner";

const cellHas = (row, idx) =>
  idx != null && ((row && row[idx]) || "").trim() !== "";

// A row is enrichable with a website/domain or a company LinkedIn URL.
const rowUsable = (row, m) =>
  cellHas(row, m.companyDomain) || cellHas(row, m.companyLinkedin);

const hasAccountColumns = mapping =>
  mapping.companyDomain != null || mapping.companyLinkedin != null;

const fieldVal = (r, key) => (r.fields && r.fields[key]) || "";

const textCol = (header, key) => ({
  header,
  cell: r => {
    const v = fieldVal(r, key);
    return v ? (
      <span className="dtTrunc" title={v}>
        {v}
      </span>
    ) : (
      "-"
    );
  }
});

const linkCol = (header, key) => ({
  header,
  cell: r =>
    fieldVal(r, key) ? (
      <a href={fieldVal(r, key)} target="_blank" rel="noopener noreferrer">
        View
      </a>
    ) : (
      "-"
    )
});

const ACCOUNT_STATS = (counts, job) => [
  { label: "Enriched", value: counts.found || 0 },
  { label: "Not found", value: counts.notFound || 0 },
  ...(counts.skipped ? [{ label: "Skipped", value: counts.skipped }] : []),
  { label: "Credits used", value: (job && job.creditsCharged) || 0, brand: true }
];

// Standard columns (always shown), matching the CSV. Long cells are width-limited.
const STD_COLUMNS = [
  { header: "Status", cell: r => <StatusPill status={r.emailStatus} /> },
  textCol("Company name", "name"),
  textCol("Legal name", "legalName"),
  textCol("Domain", "domain"),
  linkCol("Website", "website"),
  linkCol("LinkedIn", "linkedin"),
  linkCol("Facebook", "facebook"),
  linkCol("Twitter", "twitter"),
  linkCol("Crunchbase", "crunchbase"),
  {
    header: "Company email",
    cell: r =>
      r.fields && r.fields.companyEmail ? (
        <a href={`mailto:${r.fields.companyEmail}`}>{r.fields.companyEmail}</a>
      ) : (
        "-"
      )
  },
  textCol("Company phone", "companyPhone"),
  textCol("Industry", "industry"),
  textCol("All industries", "industries"),
  textCol("SIC", "sic"),
  textCol("NAICS", "naics"),
  textCol("Type", "type"),
  textCol("Founded", "founded"),
  textCol("Employees", "employees"),
  textCol("Employee range", "employeeRange"),
  textCol("Revenue", "revenue"),
  textCol("HQ city", "hqCity"),
  textCol("HQ state", "hqState"),
  textCol("HQ country", "hqCountry"),
  textCol("HQ address", "hqAddress"),
  textCol("Locations", "locations"),
  textCol("Description", "description")
];

// Advanced columns — only shown when the run opted into advanced fields.
const ADV_COLUMNS = [
  textCol("Total funding", "totalFunding"),
  textCol("Last funding", "lastFunding"),
  textCol("Funding rounds", "fundingRounds"),
  textCol("Investors", "investors"),
  textCol("Funding type", "fundingType"),
  textCol("Lead investors", "leadInvestors"),
  textCol("IPO", "ipo"),
  textCol("Technologies", "technologies")
];

const buildColumns = job => [
  ...STD_COLUMNS,
  ...(job && job.advancedFields ? ADV_COLUMNS : [])
];

const CONFIG = {
  toolKey: "ENRICH_ACCOUNT",
  iconKey: "enrich-accounts",
  title: "Enrich accounts",
  description:
    "Look up a company by its website or LinkedIn URL, then fill in its industry, size, revenue, location, founded year and more. 1 credit per account we can enrich; no match is free.",
  fields: ENRICH_ACCOUNT_FIELDS,
  endpoint: URLS.dataToolkitEnrichAccount,
  runNoun: "Enrich accounts run",
  runLabel: "Enrich accounts",
  foundLabel: "Enriched",
  inputSub:
    "Paste rows or upload a CSV. Identify each company by its website (or domain), or its LinkedIn URL.",
  pastePlaceholder:
    "website, linkedin url\nstripe.com, https://www.linkedin.com/company/stripe",
  mappingHint: (
    <>
      Tell us how to find each company: <b>a website</b> (or domain), or{" "}
      <b>a company LinkedIn URL</b>.
    </>
  ),
  requiredCheck: hasAccountColumns,
  requiredError: "Map a website (or domain), or a company LinkedIn URL.",
  rowCredits: (row, m) => (rowUsable(row, m) ? 1 : 0),
  advancedOption: {
    label: "Include advanced fields (funding, investors, technologies)",
    note: "+1 credit per match",
    extraCreditPerMatch: 1
  },
  costNote: (max, advanced) => (
    <>
      This run will use up to <b>{max.toLocaleString()}</b> credit
      {max === 1 ? "" : "s"}
      {advanced ? " (2 per match with advanced fields)" : ""}. You're only
      charged for companies we can enrich; no match is free.
    </>
  ),
  stats: ACCOUNT_STATS,
  columns: buildColumns
};

const EnrichAccounts = () => <ToolRunner cfg={CONFIG} />;

export default EnrichAccounts;
